/**
 * WUA Fast Match — Results view (Stage 04).
 *
 * Renders the immutable snapshot returned by
 * `POST /api/beta/course-fit/session/{sid}/complete` (or subsequently by
 * `GET .../result`). No fresh scoring, no LLM prose, no admissions
 * language. The UI ONLY fills approved template sentences from the
 * snapshot's strengths/considerations bullets and displays the summary
 * text authored in the anchor-profile JSON.
 *
 * Display rules (`04_UX_AND_COPY_SPEC.md` §Results):
 *   • Rounded integer only, no percent, no probability language.
 *   • Rank 1 → "Leading match".
 *   • Any other top-5 within 2.5 of rank 1's full-precision score → "Close match".
 *   • Others → "Also worth exploring".
 *   • No absolute bands (Exceptional/Strong/Weak). No red-on-fail styling.
 *   • Group-component disclosure is optional and collapsed by default.
 *
 * §Explore related subjects:
 *   • Only unprofiled subjects from families present in the top-5.
 *   • No score, no rank, no label, no reason, no group component.
 *   • Beta label "Not directly scored in this beta".
 *
 * Snapshot immunity is enforced by the backend: the `relatedFamilies`
 * list is baked into the snapshot at completion time from the taxonomy
 * pinned by that snapshot's `modelVersions.taxonomyVersion`.
 */
import React, { useEffect, useRef, useState } from "react";
import { ArrowLeftRight, ChevronDown, Check, FlaskConical, Info, Printer, Share2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { usePublishedGuides } from "@/lib/subjectGuides";
import {
  fetchBetaConfig, submitFeedback, trackCourseFitEvent,
  FAST_MATCH_CLIENT_EVENTS,
} from "@/pages/beta/courseFitApi";
import { useJourney } from "@/context/JourneyContext";
import { SavedResultBar, InterestPools, NextSteps } from "@/pages/beta/FastMatchNext";
import { DeclaredInterestPools } from "@/pages/beta/FastMatchInterests";
import { ProximityPreferenceForm } from "@/pages/beta/ProximityPreferenceForm";
import { sectorForSubject } from "@/lib/subjectSectors";
import AhaRibbon from "@/components/AhaRibbon";
import ConfirmPillarPanel from "@/components/ConfirmPillarPanel";
import { FamilyPreviewPanel } from "@/pages/beta/FamilyPreviewPanel";
import { SwapPicker, UnmatchedPicks } from "@/pages/beta/PickReview";
import { useRestoredPicks } from "@/pages/beta/usePlanPicks";
import { familySlug, guideHref, saveDraft, saveGuideCtx } from "@/lib/fastFitGuideContext";


/* ================================================================== */
/* Small utilities                                                     */
/* ================================================================== */

const LABEL_STYLES = {
  "Leading match":         "border-slate-900 bg-slate-900 text-white",
  "Close match":           "border-slate-700 bg-white text-slate-900",
  "Also worth exploring":  "border-slate-300 bg-white text-slate-600",
};

// Per §Results "existing WUA subject/family exploration action WHERE
// AVAILABLE": the current WUA frontend does not expose a
// `/subjects/family/<slug>` route (verified against `App.js` routes).
// Rather than invent one, we omit the action per the spec's escape
// clause. If a dedicated family route is added later, wire it via the
// release JSON so the URL is contract-versioned.


/* ================================================================== */
/* Result card                                                          */
/* ================================================================== */

function ResultCard({ entry, isFirst, onExpand, onReadGuide }) {
  const [open, setOpen] = useState(false);
  const guide = usePublishedGuides().bySubjectId.get(entry.subjectId);
  const {
    rank, subjectId, canonicalSubject, family, displayScore, label,
    summary, strengths = [], considerations = [], groupResults = {},
  } = entry;

  const handleToggle = () => {
    setOpen((o) => !o);
  };
  useEffect(() => {
    // Fire the analytics only when the disclosure transitions to open.
    // Placed in an effect (not the setState updater) so React 18
    // StrictMode's double-invocation of state updaters cannot double-
    // emit the `course_fit_result_opened` event.
    if (open && onExpand) onExpand({ rank, subjectId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <article
      data-testid={`result-card-rank-${rank}`}
      className={cn(
        "rounded-lg border bg-white",
        "border-slate-200",
        isFirst && "border-slate-900 shadow-sm",
        "print:break-inside-avoid print:border-slate-400 print:shadow-none",
      )}
    >
      <div className="flex items-start gap-4 p-5 sm:p-6">
        <div
          aria-hidden="true"
          data-testid={`result-score-${rank}`}
          className={cn(
            "flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-md border-2",
            isFirst ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-900",
          )}
        >
          <span className="text-2xl font-semibold leading-none">
            {displayScore}
          </span>
          <span className={cn("text-[9px] uppercase tracking-wider mt-0.5",
            isFirst ? "text-white/80" : "text-slate-500")}>
            fit
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-500"
              data-testid={`result-rank-label-${rank}`}>
              Rank {rank}
            </span>
            <span
              data-testid={`result-ordinal-${rank}`}
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-[1px] text-[11px] font-medium",
                LABEL_STYLES[label] || "border-slate-300 bg-white text-slate-600",
              )}
            >
              {label}
            </span>
            <span className="sr-only">
              Course Fit score {displayScore} out of 100.
            </span>
          </div>
          <h2
            className="mt-1 text-lg font-semibold text-slate-900 sm:text-xl break-words"
            data-testid={`result-subject-${rank}`}
          >
            {canonicalSubject}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500" data-testid={`result-family-${rank}`}>
            {family}
          </p>
          {summary && (
            <p className="mt-3 text-sm text-slate-700"
               data-testid={`result-summary-${rank}`}>
              {summary}
            </p>
          )}
          {guide && (
            <Link to={onReadGuide ? guideHref(guide.slug, familySlug(family)) : `/subjects/${guide.slug}`} data-testid={`result-guide-link-${rank}`}
              onClick={onReadGuide ? () => onReadGuide(familySlug(family)) : undefined}
              className="mt-3 inline-flex text-sm font-semibold text-[#175d51] underline-offset-4 hover:underline print:hidden">
              Read the {canonicalSubject} subject guide →
            </Link>
          )}
        </div>
      </div>

      {(strengths.length > 0 || considerations.length > 0) && (
        <div className="grid gap-4 border-t border-slate-100 p-5 sm:grid-cols-2 sm:p-6">
          {strengths.length > 0 && (
            <div data-testid={`result-strengths-${rank}`}>
              <h3 className="text-[11px] uppercase tracking-wider text-slate-500">
                Why it matches
              </h3>
              <ul className="mt-2 space-y-2">
                {strengths.map((b) => (
                  <li
                    key={`s-${b.dimensionCode}`}
                    data-testid={`result-strength-${rank}-${b.dimensionCode}`}
                    className="flex items-start gap-2 text-sm text-slate-800"
                  >
                    <span aria-hidden="true" className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-900" />
                    <span>{b.sentence}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {considerations.length > 0 && (
            <div data-testid={`result-considerations-${rank}`}>
              <h3 className="text-[11px] uppercase tracking-wider text-amber-800">
                Things to consider
              </h3>
              <ul className="mt-2 space-y-2">
                {considerations.map((b) => (
                  <li
                    key={`c-${b.dimensionCode}`}
                    data-testid={`result-consideration-${rank}-${b.dimensionCode}`}
                    className="flex items-start gap-2 text-sm text-slate-800"
                  >
                    <span aria-hidden="true" className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                    <span>{b.sentence}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-3 sm:px-6 print:hidden">
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={open}
          aria-controls={`components-${rank}`}
          data-testid={`result-components-toggle-${rank}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 rounded"
        >
          {open ? "Hide" : "Show"} group components
          <ChevronDown className={cn("h-3 w-3 transition-transform motion-reduce:transition-none",
            open && "rotate-180")} aria-hidden="true" />
        </button>
      </div>

      {open && (
        <dl
          id={`components-${rank}`}
          data-testid={`result-components-${rank}`}
          className="grid grid-cols-3 gap-4 border-t border-slate-100 bg-slate-50 p-5 text-xs sm:p-6 print:hidden"
        >
          {["Knowledge Domain", "Study Activity", "RIASEC"].map((g) => {
            const gr = groupResults[g] || {};
            return (
              <div key={g}>
                <dt className="text-[10px] uppercase tracking-wider text-slate-500">
                  {g}
                </dt>
                <dd className="mt-1 text-sm text-slate-900">
                  {Math.round(gr.groupFit ?? 0)}
                </dd>
                <dd className="text-[10px] text-slate-500">
                  demand {Math.round(gr.demandCompatibility ?? 0)} · fit {Math.round(gr.priorityFulfilment ?? 0)}
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      {/* PRINT-ONLY twin: mirrors the exact three integers the on-screen
          disclosure exposes today (groupFit / demandCompatibility /
          priorityFulfilment). Not visible on screen — hidden print:grid
          keeps it out of the on-screen a11y tree while making the
          printed output complete (the "Show group components" toggle
          is a screen-only affordance). No new metric, no hidden
          dimension: values are read from the same immutable snapshot
          object the on-screen disclosure reads from. NOTE: no
          aria-hidden — the global @media print rule zeroes
          aria-hidden elements; `hidden` (display:none) already keeps
          this out of the on-screen a11y tree. */}
      <dl
        data-testid={`result-components-print-${rank}`}
        className="hidden print:grid grid-cols-3 gap-4 border-t border-slate-300 bg-white p-5 text-xs"
      >
        {["Knowledge Domain", "Study Activity", "RIASEC"].map((g) => {
          const gr = groupResults[g] || {};
          return (
            <div key={g}>
              <dt className="text-[10px] uppercase tracking-wider text-slate-500">
                {g}
              </dt>
              <dd className="mt-1 text-sm text-slate-900">
                {Math.round(gr.groupFit ?? 0)}
              </dd>
              <dd className="text-[10px] text-slate-500">
                demand {Math.round(gr.demandCompatibility ?? 0)} · fit {Math.round(gr.priorityFulfilment ?? 0)}
              </dd>
            </div>
          );
        })}
      </dl>
    </article>
  );
}


/* ================================================================== */
/* Related subjects (exploration only)                                  */
/* ================================================================== */

function RelatedSubjects({ relatedFamilies, pickedKeys, togglePick, capReached, settled }) {
  const [open, setOpen] = useState(false);
  if (!relatedFamilies || relatedFamilies.length === 0) return null;
  if (settled && !open) {
    const added = relatedFamilies.flatMap((f) => f.unprofiledSubjects).filter((s) => pickedKeys?.has?.(s.subjectId || s.canonicalName)).map((s) => s.canonicalName);
    const total = relatedFamilies.reduce((n, f) => n + f.unprofiledSubjects.length, 0);
    return (
      <section className="mt-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 print:hidden" data-testid="related-subjects-collapsed">
        <div className="min-w-0">
          <h2 className="font-editorial text-lg tracking-tight text-slate-900">Explore related subjects</h2>
          <p className="mt-0.5 text-sm text-slate-600" data-testid="related-subjects-collapsed-summary">
            Your subjects are confirmed. {added.length ? <>From this list you added <b className="font-semibold text-slate-800">{added.join(", ")}</b>.</> : "You didn't add any from this list."} {total} related subjects in {relatedFamilies.length} families.
          </p>
        </div>
        <button type="button" onClick={() => setOpen(true)} data-testid="related-subjects-show"
          className="inline-flex min-h-[40px] items-center gap-1 rounded-full border border-slate-300 px-4 text-sm text-slate-800 transition-colors hover:border-[#7C3AED] hover:text-[#7C3AED]">
          Show <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </button>
      </section>
    );
  }
  return (
    <section className="mt-10 print:break-inside-avoid" data-testid="related-subjects">
      <h2 className="text-xl font-editorial tracking-tight text-slate-900 sm:text-2xl">
        Explore related subjects
        {settled && (
          <button type="button" onClick={() => setOpen(false)} data-testid="related-subjects-hide"
            className="ml-3 align-middle font-sans text-sm text-slate-600 underline-offset-2 hover:underline">Hide</button>
        )}
      </h2>
      <p className="mt-1 text-sm text-slate-600" data-testid="related-subjects-caption">
        These subjects sit in the same families as your top matches. They are
        <span className="mx-1 font-medium">not directly scored in this beta</span>
        — tick any you'd also consider studying and they'll be carried into
        your journey.
      </p>
      <div className="mt-4 space-y-6">
        {relatedFamilies.map((fam) => (
          <div key={fam.family} data-testid={`related-family-${fam.family}`}>
            <h3 className="text-sm font-semibold text-slate-800">{fam.family}</h3>
            <ul className="mt-2 flex flex-wrap gap-2">
              {fam.unprofiledSubjects.map((s) => {
                const key = s.subjectId || s.canonicalName;
                const chosen = pickedKeys?.has?.(key);
                const disabled = !chosen && capReached;
                return (
                  <li key={s.subjectId} className="max-w-full min-w-0">
                    <button
                      type="button"
                      onClick={() => togglePick?.(key)}
                      disabled={disabled}
                      aria-pressed={!!chosen}
                      data-testid={`related-subject-toggle-${s.subjectId}`}
                      className={[
                        "inline-flex max-w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-3 py-1.5 text-xs transition-colors",
                        chosen
                          ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                          : disabled
                            ? "bg-stone-50 text-stone-400 border-stone-200 cursor-not-allowed"
                            : "bg-white text-slate-800 border-slate-200 hover:border-[#7C3AED] hover:text-[#7C3AED]",
                      ].join(" ")}
                      title={disabled ? "Cap reached — untick a subject first" : chosen ? "Remove from my subjects" : "Add to my subjects"}
                    >
                      <span
                        data-testid={`related-subject-${s.subjectId}`}
                        className="break-words font-medium"
                      >
                        {s.canonicalName}
                      </span>
                      <span className={[
                        "text-[10px] uppercase tracking-wider",
                        chosen ? "text-white/80" : "text-slate-500",
                      ].join(" ")}>
                        {chosen ? "Chosen" : "+ Add"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}


/* ================================================================== */
/* Header + limitations                                                 */
/* ================================================================== */

function ResultsHeader({ snapshotId, completedAt, headingRef }) {
  const date = completedAt ? new Date(completedAt) : null;
  return (
    <header className="mb-6" data-testid="results-header">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500">
        <FlaskConical className="h-3 w-3" aria-hidden="true" />
        WUA Fast Match
        <span className="ml-1 inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-[1px] text-[10px] font-medium uppercase tracking-wider text-amber-900">
          Beta
        </span>
      </div>
      <h1
        ref={headingRef}
        tabIndex={-1}
        data-testid="results-heading"
        className="mt-3 font-editorial text-4xl sm:text-5xl tracking-tight text-slate-900 leading-[1.05] focus:outline-none"
      >
        Your Course Fit results
      </h1>
      <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base">
        Course Fit compares your answers with 57 calibrated subject profiles.
        A higher Fit means the study experience is more likely to feel right
        for you — it is not a probability, a grade prediction, or an
        admissions chance.
      </p>
      <p className="mt-2 text-[11px] text-slate-400" data-testid="results-snapshot-id">
        Result reference: {snapshotId}
        {date && (
          <>
            {" · "}
            {date.toISOString().slice(0, 10)}
          </>
        )}
      </p>
    </header>
  );
}


function LimitationBanner({ leadingCoverage, showCoverageAdvisory, threshold }) {
  return (
    <div
      role="note"
      data-testid="results-limitations"
      className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 print:break-inside-avoid print:border-slate-400 print:bg-white"
    >
      <div className="flex items-start gap-2">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
        <div>
          <p>
            <span className="font-medium">Fast Match measures Course Fit only.</span>{" "}
            Academic Alignment (whether your grades fit an entry standard) and
            Admissions Readiness (whether an offer is likely) are assessed
            separately elsewhere on WUA.
          </p>
          {showCoverageAdvisory && (
            <p className="mt-2 text-amber-900" data-testid="results-low-coverage">
              You answered a small share of the profile dimensions this beta
              relies on ({Math.round(leadingCoverage * 100)}% coverage on your
              leading match, threshold {Math.round(threshold * 100)}%). Treat
              this result as a starting point.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


function ClosePairBanner({ closePair, top5 }) {
  if (!closePair || !top5 || top5.length < 2) return null;
  return (
    <div
      role="note"
      data-testid="results-close-pair"
      className="mt-4 rounded-lg border border-slate-300 bg-white p-4 text-sm text-slate-800"
    >
      <p>
        <span className="font-medium">Two matches sit very close.</span>{" "}
        {top5[0].canonicalSubject} and {top5[1].canonicalSubject} scored
        within 2.5 points. Read both {top5[0].canonicalSubject.toLowerCase()}{" "}
        and {top5[1].canonicalSubject.toLowerCase()} profiles before you
        decide which resonates.
      </p>
    </div>
  );
}


/* ================================================================== */
/* Feedback panel (Stage 05)                                            */
/* ================================================================== */

// Reason copy is UI-only presentation of the server-side codes; the
// codes themselves are fetched from `/beta/course-fit/beta/config` so
// the backend allowlist stays the source of truth.
const REASON_LABELS = {
  top_match_feels_right:        "The top match feels right",
  top_match_feels_off:          "The top match feels off",
  not_enough_options:           "There weren't enough options",
  vignettes_hard_to_choose:     "The two vignettes were hard to choose between",
  expected_different_subjects:  "I expected different subjects to appear",
  found_something_new:          "I found a subject I hadn't considered",
  questions_felt_long:          "The questions felt long",
  questions_felt_short:         "The questions felt short",
  other:                        "Something else",
};

const FEEDBACK_COMMENT_MAX = 500;

function FeedbackPanel({ session }) {
  const [config, setConfig] = useState(null);
  const [rating, setRating] = useState(null);
  const [reasons, setReasons] = useState(new Set());
  const [comment, setComment] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done | error
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      const r = await fetchBetaConfig();
      if (!live) return;
      if (r.ok) setConfig(r.data.feedback);
    })();
    return () => { live = false; };
  }, []);

  const toggleReason = (code) => {
    setReasons((prev) => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code);
      else n.add(code);
      return n;
    });
  };

  const commentTrimmed = comment.slice(0, FEEDBACK_COMMENT_MAX);
  const commentOver = comment.length > FEEDBACK_COMMENT_MAX;

  const submit = async () => {
    if (!rating || state === "submitting") return;
    if (!session?.sessionId || !session?.accessToken) {
      setState("error");
      setErrorMsg("This session is no longer available for feedback.");
      return;
    }
    setState("submitting");
    setErrorMsg(null);
    const r = await submitFeedback(session.sessionId, session.accessToken, {
      usefulness: rating,
      reasons: Array.from(reasons),
      comment: commentTrimmed,
    });
    if (!r.ok) {
      setState("error");
      setErrorMsg(r.error?.message || "We couldn't save your feedback just now. Please try again.");
      return;
    }
    setState("done");
  };

  const allowedReasons = config?.reasons || Object.keys(REASON_LABELS);

  if (state === "done") {
    return (
      <section
        className="mt-10 rounded-lg border border-slate-200 bg-white p-5 sm:p-6"
        aria-labelledby="feedback-heading"
        data-testid="course-fit-feedback-thanks"
      >
        <h2 id="feedback-heading" className="text-lg font-semibold text-slate-900">
          Thanks — feedback recorded.
        </h2>
        <p className="mt-2 text-sm text-slate-700">
          Your rating helps the team refine which questions surface the
          right subjects. We'll fold it into the next release review.
        </p>
      </section>
    );
  }

  return (
    <section
      className="mt-10 rounded-lg border border-slate-200 bg-white p-5 sm:p-6"
      aria-labelledby="feedback-heading"
      data-testid="course-fit-feedback"
    >
      <h2 id="feedback-heading" className="text-lg font-semibold text-slate-900">
        Was this useful?
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Your rating shapes the next iteration of Fast Match. All fields
        below are optional except the rating.
      </p>

      <fieldset className="mt-5" data-testid="feedback-rating">
        <legend className="text-[11px] uppercase tracking-wider text-slate-500">
          Rate 1 (not useful) to 5 (very useful) <span aria-hidden="true" className="text-slate-400">*</span>
        </legend>
        <div className="mt-2 flex items-center gap-2" role="radiogroup" aria-required="true">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              onClick={() => setRating(n)}
              data-testid={`feedback-rating-${n}`}
              className={cn(
                "h-10 w-10 rounded-md border text-sm font-medium",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2",
                rating === n
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-300 bg-white text-slate-800 hover:border-slate-500",
              )}
            >
              {n}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mt-6" data-testid="feedback-reasons">
        <legend className="text-[11px] uppercase tracking-wider text-slate-500">
          Anything specific? (optional)
        </legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {allowedReasons.map((code) => {
            const checked = reasons.has(code);
            return (
              <label
                key={code}
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm",
                  checked ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-400",
                )}
                data-testid={`feedback-reason-${code}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleReason(code)}
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                  aria-label={REASON_LABELS[code] || code}
                />
                <span className="min-w-0 text-slate-800">
                  {REASON_LABELS[code] || code}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-6">
        <label
          htmlFor="feedback-comment"
          className="text-[11px] uppercase tracking-wider text-slate-500"
        >
          Free-text comment (optional, up to {FEEDBACK_COMMENT_MAX} characters)
        </label>
        <textarea
          id="feedback-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={FEEDBACK_COMMENT_MAX}
          rows={4}
          data-testid="feedback-comment"
          className="mt-2 w-full rounded-md border border-slate-300 bg-white p-3 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
          aria-describedby="feedback-comment-count"
        />
        <p
          id="feedback-comment-count"
          data-testid="feedback-comment-count"
          className={cn("mt-1 text-xs", commentOver ? "text-red-700" : "text-slate-500")}
          aria-live="polite"
        >
          {comment.length}/{FEEDBACK_COMMENT_MAX}
        </p>
      </div>

      {errorMsg && (
        <div
          role="alert"
          data-testid="feedback-error"
          className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          {errorMsg}
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!rating || state === "submitting"}
        data-testid="feedback-submit"
        className={cn(
          "mt-6 inline-flex items-center gap-1.5 rounded px-4 py-2 text-sm font-medium",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2",
          rating && state !== "submitting"
            ? "bg-slate-900 text-white hover:bg-slate-800"
            : "cursor-not-allowed bg-slate-200 text-slate-500",
        )}
      >
        {state === "submitting" ? "Sending…" : "Send feedback"}
      </button>
    </section>
  );
}


/* ================================================================== */
/* Voluntary counsellor share (Step 2)                                  */
/* ================================================================== */

function ShareWithCounsellorPanel({ session }) {
  const [state, setState] = React.useState(null); // {share_status, school, ...} | null
  const [busy, setBusy] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const [feedback, setFeedback] = React.useState(null); // "shared" | "withdrawn" | null
  const [ageOk, setAgeOk]       = React.useState(false); // under-18 gate; must tick to enable share

  React.useEffect(() => {
    let cancelled = false;
    fetch("/api/course-fit/invite/share/self", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setState(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!state || !state.invite_completed) return null;
  if (dismissed && state.share_status === "not_shared") return null;

  const doShare = async () => {
    setBusy(true);
    try {
      const r = await fetch("/api/course-fit/invite/share", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        // Session credentials are OPTIONAL server-side — the
        // invite cookie is sufficient auth. We still forward them
        // when present so the strict path is exercised in tests.
        body: JSON.stringify({
          session_id: session?.sessionId,
          access_token: session?.accessToken,
          consent_wording_version: state.consent_wording_version,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        setState((s) => ({ ...s, share_status: d.share_status,
                                   consented_at: d.consented_at }));
        setFeedback("shared");
      } else {
        setFeedback("error");
      }
    } catch {
      setFeedback("error");
    } finally { setBusy(false); }
  };

  const doWithdraw = async () => {
    setBusy(true);
    try {
      const r = await fetch("/api/course-fit/invite/share/withdraw", {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (r.ok) {
        setState((s) => ({ ...s, share_status: "withdrawn" }));
        setFeedback("withdrawn");
      } else {
        setFeedback("error");
      }
    } catch {
      setFeedback("error");
    } finally { setBusy(false); }
  };

  const school = state.school || "your school";

  // NOT SHARED — offer the consent panel (unless the user dismissed).
  if (state.share_status === "not_shared" || state.share_status === "withdrawn") {
    return (
      <section
        className="print:hidden mb-10 rounded-3xl bg-indigo-50 ring-2 ring-indigo-200/80 px-6 py-6 sm:px-8 sm:py-7 shadow-[0_1px_0_rgba(79,70,229,0.04),0_20px_40px_-24px_rgba(79,70,229,0.25)]"
        data-testid="fm-share-panel"
        aria-labelledby="fm-share-heading"
      >
        <div className="flex items-start gap-4">
          <div
            aria-hidden="true"
            className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm ring-4 ring-white/70"
          >
            <Share2 className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="fm-share-heading"
              className="text-[15px] font-semibold tracking-tight text-indigo-950"
            >
              Share with your counsellor at {school}
            </h2>
            <p className="mt-2 max-w-prose text-sm leading-relaxed text-indigo-900/80">
              They'll see the recommendations shown on this page — not your
              individual questionnaire answers. You can stop sharing at any
              time.
            </p>
            <p
              className="mt-3 max-w-prose rounded-lg bg-white/70 px-3 py-2 text-[12px] leading-relaxed text-indigo-900/85 ring-1 ring-indigo-200/70"
              data-testid="fm-share-under18-notice"
            >
              This flow is for students aged 18 and over. If you're under 18,
              please close this window and speak to your parent or guardian
              before sharing any results.
            </p>
            <label
              className="mt-3 flex items-start gap-2 text-[12px] leading-relaxed text-indigo-950 cursor-pointer select-none"
              data-testid="fm-share-age-check-label"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-3.5 w-3.5 rounded border-indigo-400 text-indigo-600 focus:ring-indigo-500"
                checked={ageOk}
                onChange={(e) => setAgeOk(e.target.checked)}
                data-testid="fm-share-age-check"
                disabled={busy}
              />
              I confirm I am 18 or over.
            </label>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={doShare}
                disabled={busy || !ageOk}
                data-testid="fm-share-consent"
                className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Share2 className="h-3.5 w-3.5" aria-hidden="true" />
                {busy ? "Sharing…" : "Share my results"}
              </button>
              <button
                type="button"
                onClick={() => setDismissed(true)}
                disabled={busy}
                data-testid="fm-share-decline"
                className="rounded-full px-4 py-2.5 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100/70 hover:text-indigo-900 disabled:opacity-60"
              >
                Not now
              </button>
            </div>
            {feedback === "withdrawn" && (
              <p
                className="mt-4 text-xs text-indigo-800/70"
                data-testid="fm-share-feedback-withdrawn"
              >
                Sharing has been stopped. Your counsellor no longer has access.
              </p>
            )}
            {feedback === "error" && (
              <p
                className="mt-4 text-xs text-red-700"
                data-testid="fm-share-feedback-error"
              >
                Sorry, that didn't go through. Please try again in a moment.
              </p>
            )}
          </div>
        </div>
      </section>
    );
  }

  // SHARED — show status + Stop sharing.
  return (
    <section
      className="print:hidden mb-10 rounded-3xl bg-emerald-50 ring-2 ring-emerald-200/80 px-6 py-6 sm:px-8 sm:py-7 shadow-[0_1px_0_rgba(5,150,105,0.04),0_20px_40px_-24px_rgba(5,150,105,0.25)]"
      data-testid="fm-share-panel-active"
    >
      <div className="flex items-start gap-4">
        <div
          aria-hidden="true"
          className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm ring-4 ring-white/70"
        >
          <Check className="h-4 w-4" />
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-semibold tracking-tight text-emerald-950">
              Shared with your counsellor at {school}
            </h2>
            <p className="mt-2 max-w-prose text-sm leading-relaxed text-emerald-900/75">
              They can see the recommendations on this page but not your
              questionnaire answers. You can stop sharing at any time.
            </p>
            {feedback === "shared" && (
              <p
                className="mt-3 text-xs text-emerald-800/80"
                data-testid="fm-share-feedback-shared"
              >
                Your counsellor can now see this page.
              </p>
            )}
            {feedback === "error" && (
              <p
                className="mt-3 text-xs text-red-700"
                data-testid="fm-share-feedback-error"
              >
                Sorry, that didn't go through. Please try again in a moment.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={doWithdraw}
            disabled={busy}
            data-testid="fm-share-withdraw"
            className="shrink-0 rounded-full border border-emerald-300 bg-white px-4 py-2 text-xs font-medium text-emerald-900 transition hover:bg-emerald-50 disabled:opacity-60"
          >
            {busy ? "Working…" : "Stop sharing"}
          </button>
        </div>
      </div>
    </section>
  );
}


/* ================================================================== */
/* Main results view                                                    */
/* ================================================================== */

export default function CourseFitResults({ snapshot, onStartOver, session, saved, planFrom }) {
  const [anchors, setAnchors] = useState([]);
  const top5 = snapshot?.top5 || [];
  const relatedFamilies = snapshot?.relatedFamilies || [];
  const headingRef = useRef(null);
  const { updateProfile, profile: journeyProfile, loaded: journeyLoaded } = useJourney();
  useEffect(() => {
    // Focus the H1 on mount so screen-reader users hear the results
    // heading and keyboard users start their Tab traversal in the
    // right place. Matches the interview shell's H1-focus behaviour.
    requestAnimationFrame(() => headingRef.current?.focus?.());
  }, []);

  // Persist the full top-5 + related families as `subjects_considered`
  // (not `subjects_confirmed` — that's gated by the explicit confirm
  // button below). This gives the persona context ("also considered
  // Nursing") without prematurely marking PICK complete.
  useEffect(() => {
    if (!snapshot || !top5.length) return;
    const winner = top5[0] || {};
    const winnerSubject = winner.canonicalSubject || winner.subjectId;
    if (!winnerSubject) return;
    // NOTE: `row.label` here is the RANK LABEL from the snapshot
    // ("Leading match", "Close match", "Also worth exploring") — NOT
    // the pretty subject name. The persona/admission-calc consumers
    // read `.label` expecting a human subject string, so we ONLY
    // store `canonicalSubject` (which is already the pretty name)
    // and keep the rank label separately under `rank_label`.
    const considered = top5.map((row) => ({
      slug:       row.canonicalSubject || row.subjectId,
      label:      row.canonicalSubject || row.subjectId,
      rank_label: row.label || null,
      family:     row.family || null,
      score:      row.score ?? null,
    })).filter((r) => r.slug);
    updateProfile({
      what: {
        subject:            winnerSubject,
        subject_label:      winner.canonicalSubject || winnerSubject,
        sector_hint:        sectorForSubject(winnerSubject, winner.canonicalSubject),
        family:             winner.family || null,
        subjects_considered: considered,
        related_families:   (relatedFamilies || []).slice(0, 6).map((r) => r?.family || r).filter(Boolean),
        snapshot_id:        snapshot.snapshotId || null,
        completed_at:       snapshot.completedAt || new Date().toISOString(),
      },
    });
  }, [snapshot, top5, relatedFamilies, updateProfile]);

  // State for the "Confirm my subjects" panel — starts pre-checked
  // with the top-1 so the fastest path (just accept the winner) is
  // one click. Students can toggle additional subjects up to the cap.
  const CONFIRM_CAP = 3;
  const [familyView, setFamilyView] = useState(null);
  const [familySettled, setFamilySettled] = useState(false);
  const [pickedKeys, setPickedKeys] = useState(() => {
    // Re-populate from any prior confirmation so returning users see
    // their previous state without losing existing progress.
    const priorConfirmed = journeyProfile?.what?.subjects_confirmed || [];
    if (priorConfirmed.length) {
      return new Set(priorConfirmed.map((s) => s?.slug || s));
    }
    return top5[0] ? new Set([top5[0].canonicalSubject || top5[0].subjectId]) : new Set();
  });
  const [swapFor, setSwapFor] = useState(null);
  const swapPick = (out, incoming) => setPickedKeys((prev) => {
    const next = [...prev].map((k) => (k === out ? incoming : k));
    return new Set(next);
  });
  const togglePick = (key) => {
    setPickedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (next.size < CONFIRM_CAP) next.add(key);
      return next;
    });
  };

  // Build the picked-items array in top-5-order for the confirm panel.
  // IMPORTANT: `row.label` is a RANK label ("Leading match", "Close
  // match", "Also worth exploring") — never store it as the subject's
  // display label or the persona will read back "I am leaning toward
  // studying Also worth exploring". We store the canonical subject
  // name as label and keep the rank label separately for UI hints.
  const pickableItems = [
    ...top5.map((row) => ({
      slug:       row.canonicalSubject || row.subjectId,
      label:      row.canonicalSubject || row.subjectId,
      rank_label: row.label || null,
      family:     row.family || null,
      source:     "top5",
    })),
    // "Also worth exploring" — flatten every unprofiled subject from
    // each related family into its OWN selectable chip. Previously
    // we collapsed each family into a single "family (related)" pill
    // which was neither a subject nor tickable per family member.
    ...(relatedFamilies || []).flatMap((f) =>
      (f?.unprofiledSubjects || []).map((s) => ({
        slug:       s.subjectId || s.canonicalName,
        label:      s.canonicalName || s.subjectId,
        rank_label: "Also worth exploring",
        family:     f?.family || null,
        source:     "related",
      }))
    ),
  ].filter((r) => r.slug);
  // Preview only: subjects from families explored for THIS result become addable (never scored here).
  for (const m of familyView?.historySubjects || []) {
    if (pickableItems.some((it) => it.label === m.canonicalName)) continue;
    pickableItems.push({
      slug: m.canonicalName, label: m.canonicalName, family: m.family, source: "family",
      rank_label: m.scored ? "From family exploration" : "From family exploration · not scored",
    });
  }
  const pickKeyFor = (name) => pickableItems.find((it) => it.label === name)?.slug || name;
  const picked = pickableItems.filter((it) => pickedKeys.has(it.slug));
  const { source: pickSource, editedFrom, planAt, setPlanAt } = useRestoredPicks({
    snapshot, session, planFrom, ready: journeyLoaded && familySettled,
    confirmed: journeyProfile?.what?.subjects_confirmed,
    fallbackKey: top5[0] ? top5[0].canonicalSubject || top5[0].subjectId : null,
    keyFor: pickKeyFor, setPickedKeys,
  });
  const confirmedLabels = (journeyProfile?.what?.subjects_confirmed || []).map((c) => c?.label || c?.slug || c);
  const picksSettled = !!pickSource && picked.length > 0 && picked.length === pickedKeys.size && confirmedLabels.length === picked.length
    && picked.every((it) => confirmedLabels.includes(it.label || it.slug));
  // Keep this tab's working selection (as subject names) so a guide visit and return never loses unsaved picks.
  const pickLabel = (k) => pickableItems.find((it) => it.slug === k)?.label
    || (journeyProfile?.what?.subjects_confirmed || []).find((c) => (c?.slug || c) === k)?.label || k;
  const draftNames = [...pickedKeys].map(pickLabel).join("|");
  // Guide visits from results (cards or family rows) carry the Fast Fit context when the server gate is on.
  const openGuideCtx = (family) => saveGuideCtx({
    sessionId: snapshot?.sessionId, snapshotId: snapshot?.snapshotId, family,
    selected: familyView?.choice?.mode === "families" ? familyView.choice.families : [family],
    pickable: pickableItems.map((it) => it.label),
  });
  const draftBase = useRef(null);
  useEffect(() => {
    if (!pickSource || pickSource === "error") return;
    const snap = snapshot?.snapshotId;
    const list = draftNames ? draftNames.split("|") : [];
    if (draftBase.current?.snap !== snap) {
      draftBase.current = { snap, names: draftNames };
      // Unsaved initial picks (default/confirmed/earlier plan) become this tab's working selection too.
      if (pickSource !== "plan" && pickSource !== "draft") saveDraft(snap, list, planAt);
      return;
    }
    if (draftNames !== draftBase.current.names) saveDraft(snap, list, planAt);
  }, [pickSource, draftNames, snapshot?.snapshotId]); // eslint-disable-line react-hooks/exhaustive-deps
  const onPlanSaved = (plan) => { setPlanAt(plan.updatedAt); saveDraft(snapshot?.snapshotId, plan.interests, plan.updatedAt); };
  const unmatchedPicks = pickSource && pickSource !== "error" ? [...pickedKeys].filter((k) => !pickableItems.some((it) => it.slug === k)) : [];
  const reviewBlock = !pickSource ? "Loading your saved subjects…"
    : pickSource === "error" ? "We couldn't load your saved subjects. Refresh to try again; nothing has been changed."
    : unmatchedPicks.length === 1 ? "1 more subject is waiting for review above, because this result doesn't offer it. It still counts toward your 3."
    : unmatchedPicks.length ? `${unmatchedPicks.length} more subjects are waiting for review above, because this result doesn't offer them. They still count toward your 3.` : null;

  const handleCardExpand = ({ rank, subjectId }) => {
    if (!session?.sessionId || !session?.accessToken) return;
    trackCourseFitEvent(
      session.sessionId, session.accessToken,
      FAST_MATCH_CLIENT_EVENTS.RESULT_OPENED,
      { rank, subjectId },
    );
  };

  // ─── Print / Save-as-PDF handler ─────────────────────────────────
  // Uses the browser's native window.print() — the same pattern used
  // by CohortOutlookPrint / WuaPlusReport / SharedParentReport /
  // ShortlistParentReport / Compare / AdvancedResults. Adds no new
  // dependency, opens no backend route, and reads from the immutable
  // snapshot already in React state. The button never appears without
  // a completed snapshot, so this handler is safe to define
  // unconditionally.
  const handlePrint = () => {
    if (!snapshot) return;
    const originalTitle = document.title;
    // Filesystem-safe filename hint for the browser's "Save as PDF"
    // dialog: browsers derive the default filename from document.title.
    // Owner requirement: no ".pdf" suffix (some browsers append their
    // own or refuse to overwrite the extension).
    const shortId = String(snapshot.snapshotId || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 8) || "result";
    const iso = (() => {
      try {
        const d = snapshot.completedAt ? new Date(snapshot.completedAt) : new Date();
        return isNaN(d.getTime()) ? new Date().toISOString().slice(0, 10) : d.toISOString().slice(0, 10);
      } catch (_e) { return new Date().toISOString().slice(0, 10); }
    })();
    const safeTitle = `WUA-Fast-Match-${iso}-${shortId}`;
    document.title = safeTitle;
    let restored = false;
    const restore = () => {
      if (restored) return;
      restored = true;
      document.title = originalTitle;
      try { window.removeEventListener("afterprint", restore); } catch (_e) { /* ignore */ }
    };
    try { window.addEventListener("afterprint", restore); } catch (_e) { /* ignore */ }
    // Safety net for browsers that don't reliably fire `afterprint`
    // (some iOS/Safari cases, some cancelled dialogs). Restores even
    // if the user immediately dismisses the print sheet.
    setTimeout(restore, 5000);
    try { window.print(); } catch (_e) { restore(); }
  };

  // Route-back link uses the current origin so the printed URL always
  // matches the environment the reader saw (preview, prod, locale).
  // Not a hard-coded hostname.
  const returnPath = "/beta/course-fit";
  const returnHref = (typeof window !== "undefined" && window.location?.origin)
    ? `${window.location.origin}${returnPath}`
    : returnPath;
  const printedOnIso = new Date().toISOString().slice(0, 10);
  const completedOnIso = snapshot?.completedAt
    ? new Date(snapshot.completedAt).toISOString().slice(0, 10)
    : null;

  return (
    <div className="min-h-screen bg-[#FBF8F3] print:bg-white" data-testid="course-fit-results-ready" data-alt-testid="course-fit-results">
      {/* PRINT-ONLY BRAND BAND — screen users never see this (display:
          none via `hidden` at screen media, which also excludes it
          from the accessibility tree). Repeats the on-screen H1 for
          continuity when the print engine breaks across pages and
          adds unambiguous "Completed" vs "Printed" labels. Contains
          no new information beyond what is already exposed in the
          on-screen results UI. NOTE: no aria-hidden — the global
          print stylesheet in index.css forces every aria-hidden
          element to `display:none !important` inside @media print,
          which would negate `print:block`. */}
      <div
        data-testid="results-print-header"
        className="hidden print:block px-6 pt-6 pb-4 border-b border-slate-300"
      >
        <div className="text-[10px] uppercase tracking-widest text-slate-600">
          WUA Fast Match · Beta
        </div>
        <div className="mt-1 text-2xl font-serif tracking-tight text-slate-900">
          Your Course Fit results
        </div>
        <div className="mt-2 text-[11px] text-slate-600">
          {snapshot?.snapshotId && (
            <>Result reference: {snapshot.snapshotId}<span className="mx-1">·</span></>
          )}
          {completedOnIso && (
            <>Completed: {completedOnIso}<span className="mx-1">·</span></>
          )}
          Printed: {printedOnIso}
        </div>
        {snapshot?.modelVersions && (
          <div className="mt-1 text-[11px] text-slate-500">
            Model: {snapshot.modelVersions.fastMatchVersion} ·
            Profiles: {snapshot.modelVersions.profileVersion} ·
            Explanations: {snapshot.modelVersions.explanationVersion}
          </div>
        )}
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12 print:max-w-full print:px-8 print:py-6">
        <AhaRibbon
          storageKey="wua:aha:course-fit:v1"
          testId="aha-ribbon-course-fit"
          benefit={{
            title:    "Nice — you've picked your subject.",
            subtitle: "Save this result and get city + university nudges tailored to your top match.",
          }}
          urgency={{
            title:    "Your top match lives only on this device.",
            subtitle: "Sync now so a browser wipe doesn't erase your Course Fit result.",
          }}
        />
        <ShareWithCounsellorPanel session={session} />
        <ResultsHeader
          snapshotId={snapshot?.snapshotId}
          completedAt={snapshot?.completedAt}
          headingRef={headingRef}
        />
        <SavedResultBar saved={saved} completedAt={snapshot?.completedAt} onStartOver={onStartOver} />
        <ClosePairBanner closePair={snapshot?.closePair} top5={top5} />
        {pickedKeys.size >= CONFIRM_CAP && (
          <div className="mt-6 rounded-xl border border-[#7C3AED]/30 bg-[#F5F0FF] px-4 py-3 text-sm text-slate-800 print:hidden" data-testid="coursefit-cap-notice">
            <p>You've chosen 3 subjects, the most you can pick. Remove one here, or tap "Swap in" on another subject to replace one.</p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {[...pickedKeys].map((k) => {
                const tid = String(k).toLowerCase().replace(/[^a-z0-9]+/g, "-");
                const offered = pickableItems.some((it) => it.slug === k);
                return (
                  <li key={k} className="inline-flex items-center gap-1 rounded-full border border-[#7C3AED] bg-white py-1 pl-3 pr-1 text-sm text-slate-900" data-testid={`coursefit-cap-pill-${tid}`}>
                    {pickLabel(k)}
                    {!offered && <span className="text-xs text-amber-800">(not in this result)</span>}
                    <button type="button" onClick={() => togglePick(k)} aria-label={`Remove ${pickLabel(k)}`}
                      className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-[#7C3AED] transition-colors hover:bg-[#7C3AED] hover:text-white"
                      data-testid={`coursefit-cap-remove-${tid}`}><X className="h-3.5 w-3.5" aria-hidden="true" /></button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div className="mt-6 space-y-4" data-testid="results-list">
          {top5.map((entry, i) => {
            const key = entry.canonicalSubject || entry.subjectId;
            const chosen = pickedKeys.has(key);
            const full = !chosen && pickedKeys.size >= CONFIRM_CAP;
            return (
              <div key={entry.subjectId}>
                {swapFor === key && full && (
                  <SwapPicker incoming={key} current={[...pickedKeys].map((k) => ({ key: k, label: pickLabel(k) }))}
                    onSwap={(out) => { swapPick(out, key); setSwapFor(null); }} onCancel={() => setSwapFor(null)} />
                )}
                <div className={`relative ${chosen ? "ring-2 ring-[#7C3AED]/60 ring-offset-2 rounded-2xl" : ""}`}>
                <ResultCard
                  entry={entry}
                  isFirst={i === 0}
                  onExpand={handleCardExpand}
                  onReadGuide={familyView ? openGuideCtx : null}
                />
                <button
                  type="button"
                  onClick={() => (full ? setSwapFor(swapFor === key ? null : key) : togglePick(key))}
                  aria-expanded={full ? swapFor === key : undefined}
                  data-testid={`coursefit-pick-toggle-${key}`}
                  aria-pressed={chosen}
                  className={[
                    "absolute top-3 right-3 z-10 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-medium border transition-colors",
                    chosen
                      ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                      : "bg-white text-[#7C3AED] border-[#7C3AED] hover:bg-[#7C3AED] hover:text-white",
                  ].join(" ")}
                  title={full ? "You have 3 subjects: choose one to swap out" : chosen ? "Remove from my subjects" : "Add to my subjects"}
                >
                  {chosen ? <><Check className="h-3 w-3" /> Chosen</> : full ? <><ArrowLeftRight className="h-3 w-3" aria-hidden="true" /> Swap in</> : <>+ Choose</>}
                </button>
                </div>
              </div>
            );
          })}
        </div>
        <LimitationBanner
          leadingCoverage={snapshot?.leadingCoverage}
          showCoverageAdvisory={snapshot?.showCoverageAdvisory}
          threshold={snapshot?.coverageAdvisoryThreshold ?? 0.45}
        />
        {/* Server-gated: renders nothing unless the family feature is on for this deployment. */}
        <FamilyPreviewPanel snapshot={snapshot} session={session} onView={setFamilyView}
          onSettled={() => setFamilySettled(true)} editedFrom={editedFrom} onRead={openGuideCtx}
          chosenNames={pickSource && pickSource !== "error" ? [...pickedKeys].map(pickLabel) : []}
          pick={{ pickedKeys, toggle: togglePick, capReached: pickedKeys.size >= CONFIRM_CAP, keyFor: pickKeyFor }} />

        <RelatedSubjects
          relatedFamilies={relatedFamilies}
          pickedKeys={pickedKeys}
          togglePick={togglePick}
          capReached={pickedKeys.size >= CONFIRM_CAP}
          settled={picksSettled}
        />

        {/* Confirm panel — the explicit "these are the subjects I'm
            taking forward" gate that turns the PICK chip green. */}
        {unmatchedPicks.length > 0 && (
          <UnmatchedPicks keys={unmatchedPicks} confirmed={journeyProfile?.what?.subjects_confirmed} source={pickSource} onRemove={togglePick} />
        )}
        {pickSource === "error" && (
          <p className="mt-6 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-900 print:hidden" data-testid="pick-review-load-error">{reviewBlock}</p>
        )}
        <div className={`mt-6 print:hidden ${pickSource === "error" ? "hidden" : ""}`} data-testid="coursefit-confirm-wrap">
          <ConfirmPillarPanel
            pillar="pick"
            choices={picked}
            min={1}
            cap={CONFIRM_CAP}
            renderItem={(it) => it.label || it.slug}
            nextTo="/cities/matcher"
            testId="coursefit-confirm-panel"
            blockedReason={reviewBlock}
            stayOnPage
            savedChoices={confirmedLabels}
          />
        </div>
        <NextSteps signedIn={!!saved?.signedIn} />
        <ProximityPreferenceForm signedIn={!!saved?.signedIn} onAnchors={setAnchors} />
        <DeclaredInterestPools session={session} top5={top5} anchors={anchors} />
        <InterestPools picked={pickSource === "error" ? [] : picked} top5={top5} session={session} anchors={anchors} blockedReason={reviewBlock} onSaved={onPlanSaved} />
        <div className="print:hidden" data-testid="results-feedback-wrapper">
          <FeedbackPanel session={session} />
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-6 print:hidden">
          <p className="text-[11px] text-slate-500" data-testid="results-model">
            Model: {snapshot?.modelVersions?.fastMatchVersion} ·
            Profiles: {snapshot?.modelVersions?.profileVersion} ·
            Explanations: {snapshot?.modelVersions?.explanationVersion}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {snapshot && (
              <button
                type="button"
                onClick={handlePrint}
                aria-label="Save Fast Match result as PDF"
                data-testid="results-download-pdf"
                className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
              >
                <Printer className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">Print / Save as PDF</span>
                <span className="sm:hidden">Save as PDF</span>
              </button>
            )}
            {onStartOver && (
              <button
                type="button"
                onClick={onStartOver}
                data-testid="results-start-over"
                className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
              >
                Start Fast Match again
              </button>
            )}
          </div>
        </div>

        {/* PRINT-ONLY FOOTER — factual disclaimer + route back. Uses
            the current window.origin (not a hard-coded hostname) so
            the printed URL always reflects the environment the reader
            saw. Repeated snapshot reference so a cut-off final page
            still carries the reference. NOTE: no aria-hidden — the
            global @media print rule zeroes aria-hidden elements. On
            screen this block is `display:none` via `hidden`, so it
            is invisible to screen readers regardless. */}
        <div
          data-testid="results-print-footer"
          className="hidden print:block mt-8 pt-4 border-t border-slate-300 text-[11px] text-slate-600"
        >
          <p>
            This is a preliminary Course Fit signal, not an admissions
            decision.
          </p>
          <p className="mt-1">
            Return to Fast Match:{" "}
            <span data-testid="results-print-return-link">{returnHref}</span>
          </p>
          {snapshot?.snapshotId && (
            <p className="mt-1 text-slate-500">
              Reference: {snapshot.snapshotId}
              {completedOnIso && (
                <><span className="mx-1">·</span>Completed {completedOnIso}</>
              )}
              <span className="mx-1">·</span>Printed {printedOnIso}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
